import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { CSVLink } from 'react-csv';
import useRequestLocation from './useRequestLocation';
import useUrlParams from './useUrlParams';
import { getQrData } from '../components/blocks/filters/QrFilter';
import { requestLimit } from '../constants/configs';
import {
  setIsStatisticCollecting,
  setStatisticPath
} from '../redux/slices/meQrData';
import anonymizeIp from '../services/meQr/anonymizeIp';

const INCLUDED_FIELDS = [
  'QR_Code_Name',
  'city',
  'country',
  'latitude',
  'longitude',
  'ip_addr',
  'operation_system',
  'device',
  'os',
  'unique_key',
  'unique',
  'created-at-date'
];

const useCsvDownload = ({ handleShowLoader } = {}) => {
  const { t } = useTranslation();
  const { isExportCsvActive } = useSelector((state) => state.meQrData);
  const ref = useRef(null);
  const { getCsvData } = useRequestLocation();
  const { baseUrl, isMePage } = useUrlParams();
  const dispatch = useDispatch();

  const [showPopover, setShowPopover] = useState({ show: false, target: null });
  const [csvData, setCsvData] = useState([]);

  const hidePopover = () => setShowPopover({ show: false, target: null });

  const downloadCsv = async (e, generateNew = 0) => {
    handleShowLoader?.(true);

    async function getQrDataLoop(page, acc) {
      const data = await getQrData({
        baseUrl,
        page,
        isMePage,
        limit: requestLimit
      });
      acc.push(...(isMePage ? data.items : data.entries));

      if (page >= data.totalPage) return acc;

      return getQrDataLoop(page + 1, acc);
    }

    const formatScanDate = (dateStr, timezoneData) => {
      if (!timezoneData || !dateStr) return dateStr;
      const offsetInSeconds = timezoneData.offset_dst || timezoneData.offset;
      if (!offsetInSeconds) return dateStr;
      return moment.utc(dateStr).utcOffset(offsetInSeconds / 60).format();
    };

    try {
      const csvData = await getCsvData(generateNew);
      if (csvData.status === 'creating' || csvData.status === 'error') {
        dispatch(setIsStatisticCollecting(true));
        setShowPopover({ target: e?.target, show: true });
        return;
      } else if (csvData.status === 'pending') {
        dispatch(setStatisticPath(''));
        dispatch(setIsStatisticCollecting(true));
        return;
      }

      const qrData = await getQrDataLoop(1, []);
      const modifiedCsvData = csvData.reduce((acc, item) => {
        const source = item._source;
        const entryId = source[isMePage ? 'page-id' : 'entry-id'];
        const qrEntry = qrData.find((el) => el.entryId === entryId);

        const newItem = { QR_Code_Name: qrEntry?.entryTitle || '' };
        Object.keys(source).forEach((el) => {
          if (INCLUDED_FIELDS.includes(el)) {
            if (el === 'ip_addr') {
              newItem[el] = anonymizeIp(source[el]);
            } else if (el === 'unique') {
              newItem.is_unique = source[el] ? t('scans.yes') : t('scans.no');
            } else {
              newItem[el] = source[el];
            }
          }
        });
        if (!newItem.unique_key) newItem.unique_key = '';
        if (newItem.is_unique === undefined) newItem.is_unique = '';

        const createdAt = newItem['created-at-date'];
        if (createdAt) {
          newItem.scan_date = isMePage
            ? createdAt
            : formatScanDate(createdAt, qrEntry?.timezone);
          delete newItem['created-at-date'];
        }

        acc.push(newItem);
        return acc;
      }, []);
      setCsvData(modifiedCsvData);
    } catch (error) {
      console.error(error);
    } finally {
      handleShowLoader?.(false);
    }
  };

  useEffect(() => {
    if (csvData.length) {
      ref.current.link.click();
      setCsvData([]);
    }
  }, [csvData]);

  const CsvLinkHidden = () => (
    <CSVLink
      data={csvData}
      ref={ref}
      filename="statistics.csv"
      style={{ display: 'none' }}
    />
  );

  return {
    downloadCsv,
    isExportCsvActive,
    showPopover,
    hidePopover,
    CsvLinkHidden
  };
};

export default useCsvDownload;
